import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Send, Copy, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { IMG } from "../lib/media";

export default function Register() {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", referral_code: "", invite_token: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (user && !done) navigate("/dashboard", { replace: true });
  }, [user, navigate, done]);

  useEffect(() => {
    const clean = (v) => {
      if (!v) return null;
      const t = v.trim();
      if (/^<.*>$/.test(t) || t === "") return null;
      return t;
    };
    const ref = clean(params.get("ref"));
    const name = clean(params.get("name"));
    const email = clean(params.get("email"));
    const phone = clean(params.get("phone"));
    const inv = clean(params.get("inv"));
    if (ref || name || email || phone || inv) {
      setForm((f) => ({
        ...f,
        ...(ref ? { referral_code: ref.toUpperCase() } : {}),
        ...(name ? { name } : {}),
        ...(email ? { email } : {}),
        ...(phone ? { phone } : {}),
        ...(inv ? { invite_token: inv } : {}),
      }));
    }
  }, [params]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      toast.success("Welcome to Iridescent — enjoy your 100 bonus points!");
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="register-page">
      <div className="flex items-center justify-center p-8 md:p-16 order-2 lg:order-1">
        <div className="w-full max-w-sm">
          <Link to="/" className="inline-flex mb-10">
            <img src={`${process.env.PUBLIC_URL}/logo-transparent.png`} alt="Iridescent Medical Spa" className="h-10 w-auto" />
          </Link>

          {done ? (
            <ReferralStep onDone={() => navigate("/dashboard")} />
          ) : (
            <>
              <h1 className="font-heading text-3xl font-light text-ink">Create your account</h1>
              <p className="text-[#5C6664] mt-2 text-sm">Join free and receive 100 welcome reward points.</p>
              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
                <div>
                  <Label htmlFor="name" className="text-ink">Full name</Label>
                  <Input id="name" required value={form.name} onChange={update("name")} data-testid="register-name" className="mt-2 rounded-lg h-11" placeholder="Ava Bellamy" />
                </div>
                <div>
                  <Label htmlFor="email" className="text-ink">Email</Label>
                  <Input id="email" type="email" required value={form.email} onChange={update("email")} data-testid="register-email" className="mt-2 rounded-lg h-11" placeholder="you@email.com" />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-ink">Phone (optional)</Label>
                  <Input id="phone" value={form.phone} onChange={update("phone")} data-testid="register-phone" className="mt-2 rounded-lg h-11" placeholder="(586) 555-0000" />
                </div>
                <div>
                  <Label htmlFor="password" className="text-ink">Password</Label>
                  <Input id="password" type="password" required minLength={6} value={form.password} onChange={update("password")} data-testid="register-password" className="mt-2 rounded-lg h-11" placeholder="At least 6 characters" />
                </div>
                <div>
                  <Label htmlFor="referral_code" className="text-ink">Referral code (optional)</Label>
                  <Input id="referral_code" value={form.referral_code} onChange={(e) => setForm((f) => ({ ...f, referral_code: e.target.value.toUpperCase() }))} data-testid="register-referral" className="mt-2 rounded-lg h-11 uppercase" placeholder="Have a friend's code?" />
                  <p className="text-xs text-[#8A9492] mt-1">Enter a friend's code — you'll earn 150 bonus points after your first paid visit.</p>
                </div>
                {error && <p className="text-sm text-red-500" data-testid="register-error">{error}</p>}
                <Button type="submit" disabled={loading} data-testid="register-submit"
                  className="iri-gradient text-ink rounded-full h-12 w-full font-medium hover:scale-[1.02] transition-transform">
                  {loading ? "Creating account…" : "Create account"}
                </Button>
              </form>
              <p className="mt-6 text-sm text-[#5C6664]">
                Already a member?{" "}
                <Link to="/login" className="text-ink font-medium underline" data-testid="go-to-login">Sign in</Link>
              </p>
            </>
          )}
        </div>
      </div>

      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src={IMG.facial} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/20" />
      </div>
    </div>
  );
}

function ReferralStep({ onDone }) {
  const [friends, setFriends] = useState([{ name: "", phone: "" }, { name: "", phone: "" }]);
  const [invites, setInvites] = useState(null);
  const [sending, setSending] = useState(false);

  const setFriend = (i, k, v) => setFriends((fs) => fs.map((f, idx) => (idx === i ? { ...f, [k]: v } : f)));

  const send = async () => {
    const payload = friends.filter((f) => f.name.trim());
    if (!payload.length) { toast.error("Add at least one friend's name"); return; }
    setSending(true);
    try {
      const res = await api.post("/member/referrals/invite", { friends: payload });
      setInvites(res.data.invites);
      toast.success("Invites ready — send them off!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create invites");
    } finally {
      setSending(false);
    }
  };

  const copy = (txt) => { navigator.clipboard?.writeText(txt); toast.success("Message copied — paste it to your friend!"); };

  return (
    <div data-testid="referral-step">
      <h1 className="font-heading text-3xl font-light text-ink">You're in! 🎉</h1>
      <p className="text-[#5C6664] mt-2 text-sm">
        Refer up to 2 friends now — for every friend who joins and makes their first purchase, you'll earn
        <strong className="text-ink"> 500 points</strong> (a complimentary Express AquaHydrate Facial).
      </p>

      {!invites ? (
        <div className="mt-6 space-y-4">
          {friends.map((f, i) => (
            <div key={i} className="rounded-xl bg-stone p-3 space-y-2" data-testid={`referral-friend-${i}`}>
              <Input value={f.name} onChange={(e) => setFriend(i, "name", e.target.value)} placeholder={`Friend ${i + 1} name`} className="rounded-lg h-10 bg-white" data-testid={`referral-name-${i}`} />
              <Input value={f.phone} onChange={(e) => setFriend(i, "phone", e.target.value)} placeholder="Mobile (optional)" className="rounded-lg h-10 bg-white" data-testid={`referral-phone-${i}`} />
            </div>
          ))}
          <Button onClick={send} disabled={sending} data-testid="referral-send"
            className="iri-gradient text-ink rounded-full h-12 w-full font-medium">
            <Send className="h-4 w-4 mr-2" /> {sending ? "Preparing…" : "Create invites"}
          </Button>
          <button onClick={onDone} data-testid="referral-skip" className="w-full text-sm text-[#8A9492] hover:text-ink">
            Skip for now
          </button>
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {invites.map((inv, i) => (
            <div key={i} className="rounded-xl border border-slate-200 p-3" data-testid={`referral-invite-${i}`}>
              <p className="text-sm text-ink font-medium">{inv.friend_name}</p>
              <div className="flex gap-2 mt-2">
                {inv.friend_phone && (
                  <a href={`sms:${inv.friend_phone}?&body=${encodeURIComponent(inv.sms)}`}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-full iri-gradient text-ink text-sm font-medium"
                    data-testid={`referral-text-${i}`}>
                    <Send className="h-3.5 w-3.5" /> Text {inv.friend_name.split(" ")[0]}
                  </a>
                )}
                <button onClick={() => copy(inv.sms)} data-testid={`referral-copy-${i}`}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-full bg-stone text-ink text-sm">
                  <Copy className="h-3.5 w-3.5" /> Copy message
                </button>
              </div>
            </div>
          ))}
          <Button onClick={onDone} data-testid="referral-continue"
            className="bg-ink text-white rounded-full h-12 w-full font-medium">
            Continue to my dashboard <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      )}
    </div>
  );
}
